/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import HeaderSection from '../../common/headerSection';
import PageHeader from '../../common/pageHeader';
import Pagination from '../../../widgets/pagination';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';
import {
  SterilizationUsageRecord,
  initialSterilizationUsageData,
  equipmentOptions,
  mediaBatchOptions
} from './data/sterilizationUsageData';

const SterilizationUsageReport: React.FC = () => {
  const [records] = useState<SterilizationUsageRecord[]>(initialSterilizationUsageData);
  
  // Filters
  const [selectedEquipment, setSelectedEquipment] = useState('All');
  const [selectedMediaBatch, setSelectedMediaBatch] = useState('All');
  const [dateFilterPreset, setDateFilterPreset] = useState<'all' | 'today' | '7days' | '30days'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Selected Record Modal / Details Drawer
  const [activeRecord, setActiveRecord] = useState<SterilizationUsageRecord | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handlePageChange = (newPage: any, newPageSize?: number) => {
    setPage(newPage);
    if (newPageSize) {
      setPageSize(newPageSize);
    }
  };

  // Helper to parse DD/MM/YYYY into a Date object
  const parseDDMMYYYY = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    return null;
  };

  // Helper to parse YYYY-MM-DD input string
  const parseYYYYMMDD = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    return null;
  };

  // Handle Preset Clicks
  const handlePresetDate = (preset: 'all' | 'today' | '7days' | '30days') => {
    setDateFilterPreset(preset);
    setPage(1);

    const refDate = new Date(2026, 9, 15); // Anchor to current context: Oct 15, 2026
    if (preset === 'all') {
      setDateFrom('');
      setDateTo('');
    } else if (preset === 'today') {
      const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
      const dStr = `${refDate.getFullYear()}-${pad(refDate.getMonth() + 1)}-${pad(refDate.getDate())}`;
      setDateFrom(dStr);
      setDateTo(dStr);
    } else if (preset === '7days') {
      const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
      const past7 = new Date(refDate.getTime() - 7 * 24 * 60 * 60 * 1000);
      setDateFrom(`${past7.getFullYear()}-${pad(past7.getMonth() + 1)}-${pad(past7.getDate())}`);
      setDateTo(`${refDate.getFullYear()}-${pad(refDate.getMonth() + 1)}-${pad(refDate.getDate())}`);
    } else if (preset === '30days') {
      const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
      const past30 = new Date(refDate.getTime() - 30 * 24 * 60 * 60 * 1000);
      setDateFrom(`${past30.getFullYear()}-${pad(past30.getMonth() + 1)}-${pad(past30.getDate())}`);
      setDateTo(`${refDate.getFullYear()}-${pad(refDate.getMonth() + 1)}-${pad(refDate.getDate())}`);
    }
  };

  // Reset all filters
  const handleClearFilters = () => {
    setSelectedEquipment('All');
    setSelectedMediaBatch('All');
    setDateFilterPreset('all');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
    setPage(1);
    showToast('All filters have been reset.');
  };

  // Filtered dataset
  const filteredRecords = useMemo(() => {
    return records.filter(item => {
      // Equipment filter
      if (selectedEquipment !== 'All' && item.equipmentId !== selectedEquipment) {
        return false;
      }

      // Media Batch filter
      if (selectedMediaBatch !== 'All') {
        const batchLower = selectedMediaBatch.toLowerCase();
        const matchesBatch =
          item.mediaBatchNo.toLowerCase() === batchLower ||
          item.batchOrLotOrGirNo.toLowerCase() === batchLower;
        if (!matchesBatch) return false;
      }

      // Date Range filter
      if (dateFrom || dateTo) {
        const itemDate = parseDDMMYYYY(item.date);
        if (itemDate) {
          if (dateFrom) {
            const fromD = parseYYYYMMDD(dateFrom);
            if (fromD && itemDate < fromD) return false;
          }
          if (dateTo) {
            const toD = parseYYYYMMDD(dateTo);
            if (toD && itemDate > toD) return false;
          }
        }
      }

      // Search term
      if (searchTerm.trim()) {
        const s = searchTerm.toLowerCase().trim();
        const matches =
          item.productOrMaterialName.toLowerCase().includes(s) ||
          item.batchOrLotOrGirNo.toLowerCase().includes(s) ||
          item.testName.toLowerCase().includes(s) ||
          item.loadCycleNo.toLowerCase().includes(s) ||
          item.userName.toLowerCase().includes(s) ||
          item.remarks.toLowerCase().includes(s) ||
          item.equipmentName.toLowerCase().includes(s);
        if (!matches) return false;
      }

      return true;
    });
  }, [records, selectedEquipment, selectedMediaBatch, dateFrom, dateTo, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / pageSize) || 1;
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Export CSV function
  const handleExportCSV = () => {
    const headers = [
      'Date',
      'Product / Material Name',
      'Batch No. / Lot No. / GIR No.',
      'Test Name',
      'Load Cycle No.',
      'Analysis Start Time',
      'Temperature & Pressure Attainment Time',
      'Hold Completion Time',
      'Analysis End Time',
      'No. of Injections / Readings / Activities',
      'Remarks',
      'User Name',
      'Equipment'
    ];

    const rows = filteredRecords.map(r => [
      `"${r.date}"`,
      `"${r.productOrMaterialName.replace(/"/g, '""')}"`,
      `"${r.batchOrLotOrGirNo}"`,
      `"${r.testName}"`,
      `"${r.loadCycleNo}"`,
      `"${r.analysisStartTime}"`,
      `"${r.tempPressureAttainmentTime}"`,
      `"${r.holdCompletionTime}"`,
      `"${r.analysisEndTime}"`,
      `"${r.noOfReadingsOrActivities}"`,
      `"${r.remarks.replace(/"/g, '""')}"`,
      `"${r.userName}"`,
      `"${r.equipmentName}"` 
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Sterilization_Usage_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Sterilization Usage Report downloaded as CSV.');
  };

  return (
    <div>
      <HeaderSection />
      <PageHeader 
        pageHeader="Sterilization Usage Report"
        pageDescription="Sterilization cycle log, thermal & pressure attainment history, and equipment run accountability."
      />

      <div className="min-h-screen bg-[#F8FAFC] pb-16 font-['Inter']">
        {/* Toast Notification */}
        <AnimatePresence>
          {toastMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-5 right-6 z-50 bg-[#0F2942] text-white px-5 py-3 rounded-xl shadow-xl flex items-center gap-3 border border-slate-700"
            >
              <span className="material-symbols-outlined text-emerald-400 text-lg">check_circle</span>
              <span className="text-xs font-semibold">{toastMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Buttons */}
        <div className="max-w-[1720px] mx-auto px-6 pt-4 flex items-center justify-end gap-2.5">
          <button
            onClick={() => {
              showToast('Refreshed sterilization run records.');
            }}
            className="px-3.5 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm"
            title="Refresh dataset"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            Refresh
          </button>

          <button
            onClick={handleExportCSV}
            className="px-3.5 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm"
          >
            <span className="material-symbols-outlined text-sm text-emerald-600">file_download</span>
            Export CSV
          </button>
        </div>

        {/* Main Content Area */}
        <main className="max-w-[1720px] mx-auto px-6 pt-6 space-y-6">
        
        {/* Summary Metric Ribbon */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-[#0052CC] flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-xl">steppers</span>
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Total Sterilization Runs</p>
              <p className="text-xl font-black text-slate-800 mt-1">{filteredRecords.length}</p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-xl">verified</span>
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Attainment Compliance</p>
              <p className="text-xl font-black text-emerald-600 mt-1">100% Passed</p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-xl">timer</span>
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Standard Hold Time</p>
              <p className="text-xl font-black text-slate-800 mt-1">20.0 Min @ 121°C</p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-xl">precision_manufacturing</span>
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Active Equipments</p>
              <p className="text-xl font-black text-slate-800 mt-1">4 Qualified Units</p>
            </div>
          </div>
        </div>

        {/* SEARCH AND FILTERS */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[#0052CC] text-lg">filter_alt</span>
              <span className="text-xs font-black uppercase tracking-wider text-slate-700">
                Filter Sterilization Records
              </span>
            </div>
            <button
              onClick={handleClearFilters}
              className="text-xs font-bold text-slate-500 hover:text-[#0052CC] flex items-center gap-1 transition-colors"
            >
              <span className="material-symbols-outlined text-sm">restart_alt</span>
              Clear Filters
            </button>
          </div>

          {/* Filter Controls Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* 1. Equipment Filter */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                Equipment
              </label>
              <div className="relative">
                <select
                  value={selectedEquipment}
                  onChange={(e) => {
                    setSelectedEquipment(e.target.value);
                    setPage(1);
                  }}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 appearance-none pr-8 focus:bg-white focus:border-[#0052CC] outline-none transition-all cursor-pointer"
                >
                  {equipmentOptions.map(eq => (
                    <option key={eq.id} value={eq.id}>
                      {eq.label}
                    </option>
                  ))}
                </select>
                <span className="material-symbols-outlined absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-base">
                  expand_more
                </span>
              </div>
            </div>

            {/* 2. Media Batch Filter */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                Media Batch / Lot
              </label>
              <div className="relative">
                <select
                  value={selectedMediaBatch}
                  onChange={(e) => {
                    setSelectedMediaBatch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 appearance-none pr-8 focus:bg-white focus:border-[#0052CC] outline-none transition-all cursor-pointer"
                >
                  {mediaBatchOptions.map(mb => (
                    <option key={mb} value={mb}>
                      {mb === 'All' ? 'All Media Batches / Lots' : mb}
                    </option>
                  ))}
                </select>
                <span className="material-symbols-outlined absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-base">
                  expand_more
                </span>
              </div>
            </div>

            {/* 3. Date Presets & Custom Range */}
            <div className="lg:col-span-2">
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                Date Filters
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                  <button
                    type="button"
                    onClick={() => handlePresetDate('all')}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-all ${
                      dateFilterPreset === 'all' && !dateFrom && !dateTo
                        ? 'bg-white text-[#0052CC] shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePresetDate('today')}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-all ${
                      dateFilterPreset === 'today'
                        ? 'bg-white text-[#0052CC] shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePresetDate('7days')}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-all ${
                      dateFilterPreset === '7days'
                        ? 'bg-white text-[#0052CC] shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePresetDate('30days')}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-all ${
                      dateFilterPreset === '30days'
                        ? 'bg-white text-[#0052CC] shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Last 30 Days
                  </button>
                </div>

                <div className="flex items-center gap-1.5 flex-1 min-w-[240px]">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setDateFilterPreset('all');
                      setPage(1);
                    }}
                    placeholder="From"
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs font-semibold text-slate-700 focus:bg-white focus:border-[#0052CC] outline-none"
                  />
                  <span className="text-slate-400 text-xs">to</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setDateFilterPreset('all');
                      setPage(1);
                    }}
                    placeholder="To"
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs font-semibold text-slate-700 focus:bg-white focus:border-[#0052CC] outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Search bar row */}
          <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row items-center gap-3">
            <div className="relative flex-1 w-full">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                search
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by Product / Material, Batch / Lot / GIR No., Cycle, User, or Remarks..."
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs font-medium text-slate-800 placeholder-slate-400 focus:bg-white focus:border-[#0052CC] outline-none transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <span className="material-symbols-outlined text-xs">close</span>
                </button>
              )}
            </div>

            <div className="text-[11px] font-semibold text-slate-500 shrink-0">
              Showing <span className="font-black text-[#0052CC]">{filteredRecords.length}</span> matching cycle runs
            </div>
          </div>
        </section>

        {/* STERILIZATION USAGE REPORT TABLE */}
        <section className="bg-white shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto min-h-[400px] max-h-[400px]">
            <table className="w-full border-collapse">
              <thead>
                <tr className="tableHeadRow text-center">
                  <th className="tableHeadData2">S. No.</th>
                  <th className="tableHeadData2">Date</th>
                  <th className="tableHeadData2">Product / Material Name</th>
                  <th className="tableHeadData2">Batch No. / Lot No. / GIR No.</th>
                  <th className="tableHeadData2">Test Name</th>
                  <th className="tableHeadData2">Load Cycle No.</th>
                  <th className="tableHeadData2">Analysis Start Time</th>
                  <th className="tableHeadData2">Temperature & Pressure Attainment Time</th>
                  <th className="tableHeadData2">Hold Completion Time</th>
                  <th className="tableHeadData2">Analysis End Time</th>
                  <th className="tableHeadData2">No. of Injections / Readings / Activities</th>
                  <th className="tableHeadData2">Remarks, if any</th>
                  <th className="tableHeadData2">User Name</th>
                  <th className="tableHeadData2">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                {paginatedRecords.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="py-12 text-center text-slate-400">
                      <span className="material-symbols-outlined text-4xl mb-2 text-slate-300 block">inventory_2</span>
                      <p className="font-semibold text-sm text-slate-600">No sterilization usage records match the selected filters.</p>
                      <button
                        onClick={handleClearFilters}
                        className="mt-3 text-xs text-[#0052CC] font-bold hover:underline"
                      >
                        Reset filters to view all records
                      </button>
                    </td>
                  </tr>
                ) : (
                  paginatedRecords.map((item, idx) => {
                    const rowNumber = (page - 1) * pageSize + idx + 1;
                    return (
                      <tr 
                        key={item.id} 
                        className="hover:bg-slate-50/50 transition-colors"
                        onClick={() => setActiveRecord(item)}
                      >
                        {/* 1. S. No. */}
                        <td className="px-6 py-4 text-xs font-bold text-slate-500">
                          {rowNumber}
                        </td>

                        {/* 2. Date */}
                        <td className="px-6 py-4 text-xs font-bold text-slate-900">
                          {item.date}
                        </td>

                        {/* 3. Product / Material Name */}
                        <td className="px-6 py-4 text-xs font-bold text-[#0F2942]">
                          {item.productOrMaterialName}
                        </td>

                        {/* 4. Batch No. / Lot No. / GIR No. */}
                        <td className="px-6 py-4 text-xs font-bold text-blue-700">
                          {item.batchOrLotOrGirNo}
                        </td>

                        {/* 5. Test Name */}
                        <td className="px-6 py-4 text-xs font-bold text-slate-800">
                          {item.testName}
                        </td>

                        {/* 6. Load Cycle No. */}
                        <td className="px-6 py-4 text-xs font-bold text-slate-700">
                          {item.loadCycleNo}
                        </td>

                        {/* 7. Analysis Start Time */}
                        <td className="px-6 py-4 text-xs font-bold text-slate-700">
                          {item.analysisStartTime}
                        </td>

                        {/* 8. Temperature & Pressure Attainment Time */}
                        <td className="px-6 py-4 text-xs font-bold text-slate-800">
                          {item.tempPressureAttainmentTime}
                        </td>

                        {/* 9. Hold Completion Time */}
                        <td className="px-6 py-4 text-xs font-bold text-slate-800">
                          {item.holdCompletionTime}
                        </td>

                        {/* 10. Analysis End Time */}
                        <td className="px-6 py-4 text-xs font-bold text-slate-700">
                          {item.analysisEndTime}
                        </td>

                        {/* 11. No. of Injections / Readings / Activities */}
                        <td className="px-6 py-4 text-xs font-bold text-slate-800">
                          {item.noOfReadingsOrActivities}
                        </td>

                        {/* 12. Remarks, if any */}
                        <td className="px-6 py-4 text-xs text-slate-700">
                          {item.remarks || '—'}
                        </td>

                        {/* 13. User Name */}
                        <td className="px-6 py-4 text-xs font-bold text-slate-800">
                          {item.userName}
                        </td>

                        {/* 14. Details action */}
                        <td className="px-6 py-4 text-xs">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveRecord(item);
                            }}
                            className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all"
                            title="View Run Audit Log"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />
        </section>
      </main>

      {/* Cycle Details Modal / Drawer */}
      <AnimatePresence>
        {activeRecord && (
          <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/50 backdrop-blur-xs overflow-y-auto pt-10">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden mb-8"
            >
              {/* Modal Header */}
              <div className="bg-[#0F2942] text-white p-5 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-300">
                    <span>Sterilization Cycle Run Details</span>
                    <span>•</span>
                    <span>{activeRecord.id}</span>
                  </div>
                  <h3 className="text-base font-black tracking-tight text-white mt-1">
                    {activeRecord.productOrMaterialName}
                  </h3>
                </div>
                <button
                  onClick={() => setActiveRecord(null)}
                  className="w-8 h-8 rounded-lg bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-5 text-xs max-h-[75vh] overflow-y-auto">
                {/* General Info Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div>
                    <span className="text-[10px] font-black uppercase text-slate-400 block">Date of Run</span>
                    <span className="font-bold text-slate-800 text-sm">{activeRecord.date}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase text-slate-400 block">Batch / Lot / GIR</span>
                    <span className="font-mono font-bold text-blue-700 text-sm">{activeRecord.batchOrLotOrGirNo}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase text-slate-400 block">Equipment</span>
                    <span className="font-bold text-slate-800">{activeRecord.equipmentName}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase text-slate-400 block">Test Name</span>
                    <span className="font-bold text-slate-800">{activeRecord.testName}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase text-slate-400 block">Load Cycle No.</span>
                    <span className="font-bold text-slate-800">{activeRecord.loadCycleNo}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase text-slate-400 block">Executed By</span>
                    <span className="font-bold text-slate-800">{activeRecord.userName}</span>
                  </div>
                </div>

                {/* Thermal & Pressure Attainment Stage Progression */}
                <div className="space-y-2">
                  <h4 className="text-[11px] font-black uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-sm text-[#0052CC]">schedule</span>
                    Cycle Timeline &amp; Thermal Attainment Stages
                  </h4>

                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                      <span className="text-[9px] font-black uppercase text-slate-400 block">1. Start</span>
                      <span className="font-mono font-black text-slate-800 text-sm">{activeRecord.analysisStartTime}</span>
                      <span className="text-[9px] text-slate-500 block mt-0.5">Purge &amp; Pre-vac</span>
                    </div>

                    <div className="bg-amber-50 p-2.5 rounded-xl border border-amber-200">
                      <span className="text-[9px] font-black uppercase text-amber-600 block">2. Attainment</span>
                      <span className="font-mono font-black text-amber-800 text-sm">{activeRecord.tempPressureAttainmentTime}</span>
                      <span className="text-[9px] text-amber-700 block mt-0.5">{activeRecord.cycleTemp} / {activeRecord.cyclePressure}</span>
                    </div>

                    <div className="bg-indigo-50 p-2.5 rounded-xl border border-indigo-200">
                      <span className="text-[9px] font-black uppercase text-indigo-600 block">3. Hold Done</span>
                      <span className="font-mono font-black text-indigo-800 text-sm">{activeRecord.holdCompletionTime}</span>
                      <span className="text-[9px] text-indigo-700 block mt-0.5">{activeRecord.holdDurationMinutes}m Exposure</span>
                    </div>

                    <div className="bg-emerald-50 p-2.5 rounded-xl border border-emerald-200">
                      <span className="text-[9px] font-black uppercase text-emerald-600 block">4. End &amp; Exhaust</span>
                      <span className="font-mono font-black text-emerald-800 text-sm">{activeRecord.analysisEndTime}</span>
                      <span className="text-[9px] text-emerald-700 block mt-0.5">Chamber Released</span>
                    </div>
                  </div>
                </div>

                {/* Validation & Physical Indicators */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white p-3 rounded-xl border border-slate-200 space-y-1">
                    <span className="text-[10px] font-black uppercase text-slate-400 block">Biological Indicator (Geobacillus stearothermophilus)</span>
                    <div className="flex items-center gap-2 text-emerald-700 font-bold">
                      <span className="material-symbols-outlined text-sm">check_circle</span>
                      <span>{activeRecord.biologicalIndicator}</span>
                    </div>
                  </div>

                  <div className="bg-white p-3 rounded-xl border border-slate-200 space-y-1">
                    <span className="text-[10px] font-black uppercase text-slate-400 block">Chemical / Steam Clox Indicator</span>
                    <div className="flex items-center gap-2 text-emerald-700 font-bold">
                      <span className="material-symbols-outlined text-sm">done_all</span>
                      <span>{activeRecord.cloxIndicator}</span>
                    </div>
                  </div>
                </div>

                {/* Readings & Remarks */}
                <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase text-slate-400">Total Injections / Readings / Activities</span>
                    <span className="font-bold text-slate-800">{activeRecord.noOfReadingsOrActivities}</span>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <span className="text-[10px] font-black uppercase text-slate-400 block mb-0.5">Cycle Remarks &amp; Observations</span>
                    <p className="text-xs text-slate-700 font-medium leading-relaxed">{activeRecord.remarks}</p>
                  </div>
                </div>

                {/* Electronic Signature Block */}
                <div className="bg-blue-50/60 p-3 rounded-xl border border-blue-200 flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-blue-700">lock_open_right</span>
                    <div>
                      <p className="font-bold text-slate-800">Electronically Recorded &amp; Signed</p>
                      <p className="text-slate-500 text-[10px]">{activeRecord.userName} on {activeRecord.date} {activeRecord.analysisEndTime}</p>
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-mono font-bold text-[10px]">
                    21 CFR § 11.50 VALID
                  </span>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  onClick={() => setActiveRecord(null)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-bold text-xs"
                >
                  Close
                </button>
                <button
                  onClick={() => setActiveRecord(null)}
                  className="px-4 py-2 rounded-xl bg-[#0052CC] text-white hover:bg-[#0043A8] font-bold text-xs flex items-center gap-1.5"
                >
                  <span className="material-symbols-outlined text-sm">print</span>
                  Print Run Sheet
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
    </div>
  );
};

export default SterilizationUsageReport;
